// Global Instructions Rule Applied!

import {
  FieldTypeEnum,
  SelectTypeEnum,
  FieldConfig,
  SelectFieldConfig,
  CompanyFieldConfig
} from '../../../core/components/InputManager/types.ts'
import { RoundEntryFields } from '../../../state_manager/data_models/round_entry.ts'
import {
  LOOKUP_CATEGORY_FUNDING_STAGE,
  LOOKUP_CATEGORY_SECURITY_TYPE
} from '../../../state_manager/constants/lookupCategories.ts'

/**
 * Generates field configurations for Round form sections
 * Uses InputManager field configurations following the pattern from companies/table/utils/fieldConfigs.ts
 */

/**
 * Generates field configurations for Round Detail section
 * Field order follows the house convention (deal form's Deal Information):
 * identity → classification → currency → economics → timeline
 */
export function getRoundDetailFields(data: any, helpers: any, isReadOnly = false): FieldConfig[] {
  const { getCurrencyById, getLookupOptions, getCurrencyOptions, getCompanyDetailsById } = helpers
  const currencyId = data[RoundEntryFields.rounds__currency] || null
  const currencySymbol = getCurrencyById?.(currencyId)?.symbol || '$'
  const fundingStageOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_FUNDING_STAGE) : []
  const securityTypeOptions = getLookupOptions ? getLookupOptions(LOOKUP_CATEGORY_SECURITY_TYPE) : []
  const currencyOptions = getCurrencyOptions ? getCurrencyOptions() : []

  const fields: FieldConfig[] = []

  const roundId = data[RoundEntryFields.rounds__id] || null

  // 1. Round Name
  fields.push({
    key: RoundEntryFields.rounds__name,
    label: 'Round Name',
    type: FieldTypeEnum.TEXT,
    value: data[RoundEntryFields.rounds__name] || '',
    editable: !isReadOnly && !!roundId,
    placeholder: 'Round Name',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        name: value,
        [RoundEntryFields.rounds__name]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // 2. Account
  // Pass account ID as string so getCompanyDisplayValue can use getCompanyDetailsById to look it up
  // getCompanyDisplayValue only calls getCompanyDetailsById when value is a string, not an object
  const accountId = data[RoundEntryFields.rounds__account] || null
  const accountValue = accountId || null

  fields.push({
    key: RoundEntryFields.rounds__account,
    label: 'Account',
    type: FieldTypeEnum.COMPANY,
    value: accountValue,
    editable: !isReadOnly && !!roundId,
    getCompanyDetailsById: getCompanyDetailsById,
    placeholder: 'Account',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const accountIdValue = typeof value === 'object' && value?.value ? value.value : value
      const result: any = {
        account: accountIdValue,
        [RoundEntryFields.rounds__account]: accountIdValue
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    },
    hyperlinked: true,
    hyperlinkModule: 'companies',
    getRecordId: (value) => {
      if (!value) return null
      return typeof value === 'object' && value?.value ? value.value : value
    }
  } as CompanyFieldConfig)

  // 3. Funding Stage
  fields.push({
    key: RoundEntryFields.rounds__funding_stage,
    label: 'Funding Stage',
    type: FieldTypeEnum.SELECT,
    value: data[RoundEntryFields.rounds__funding_stage] || '',
    editable: !isReadOnly && !!roundId,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_FUNDING_STAGE,
    options: fundingStageOptions,
    placeholder: 'Funding Stage',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        funding_stage: value,
        [RoundEntryFields.rounds__funding_stage]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  } as SelectFieldConfig)

  // 4. Security Type
  fields.push({
    key: RoundEntryFields.rounds__security_type,
    label: 'Security Type',
    type: FieldTypeEnum.SELECT,
    value: data[RoundEntryFields.rounds__security_type] || '',
    editable: !isReadOnly && !!roundId,
    selectType: SelectTypeEnum.LOOKUP,
    lookupCategory: LOOKUP_CATEGORY_SECURITY_TYPE,
    options: securityTypeOptions,
    placeholder: 'Security Type',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        security_type: value,
        [RoundEntryFields.rounds__security_type]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  } as SelectFieldConfig)

  // 5. Currency (precedes the amounts it formats)
  fields.push({
    key: RoundEntryFields.rounds__currency,
    label: 'Currency',
    type: FieldTypeEnum.SELECT,
    value: data[RoundEntryFields.rounds__currency] || '',
    editable: !isReadOnly && !!roundId,
    selectType: SelectTypeEnum.FILTER,
    options: currencyOptions,
    placeholder: 'Currency',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        currency: value,
        [RoundEntryFields.rounds__currency]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  } as SelectFieldConfig)

  // 6. Amount Raising
  fields.push({
    key: RoundEntryFields.rounds__raise_amount,
    label: 'Amount Raising',
    type: FieldTypeEnum.CURRENCY,
    value: data[RoundEntryFields.rounds__raise_amount] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    placeholder: 'Amount Raising',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        raise_amount: value,
        [RoundEntryFields.rounds__raise_amount]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // 7. Pre-money Valuation
  fields.push({
    key: RoundEntryFields.rounds__pre_money_valuation,
    label: 'Pre-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: data[RoundEntryFields.rounds__pre_money_valuation] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    placeholder: 'Pre-money Valuation',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        pre_money_valuation: value,
        [RoundEntryFields.rounds__pre_money_valuation]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // 8. Post-money Valuation
  fields.push({
    key: RoundEntryFields.rounds__post_money_valuation,
    label: 'Post-money Valuation',
    type: FieldTypeEnum.CURRENCY,
    value: data[RoundEntryFields.rounds__post_money_valuation] || null,
    editable: !isReadOnly && !!roundId,
    currencySymbol: currencySymbol,
    placeholder: 'Post-money Valuation',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        post_money_valuation: value,
        [RoundEntryFields.rounds__post_money_valuation]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  // 9. Our Investment (derived, read-only — closes the economics block)
  if (data[RoundEntryFields.prog__commitment] !== undefined) {
    fields.push({
      key: RoundEntryFields.prog__commitment,
      label: 'Our Investment',
      type: FieldTypeEnum.CURRENCY,
      value: data[RoundEntryFields.prog__commitment] || null,
      editable: false, // Keep read-only as per previous requirements
      currencySymbol: currencySymbol,
      placeholder: 'Our Investment'
    })
  }

  // 10. Round Close Date (last)
  fields.push({
    key: RoundEntryFields.rounds__round_close_date,
    label: 'Round Close Date',
    type: FieldTypeEnum.DATE,
    value: data[RoundEntryFields.rounds__round_close_date] || null,
    editable: !isReadOnly && !!roundId,
    placeholder: 'Round Close Date',
    patchEndpoint: 'rounds',
    valueTransformer: (value, currentRecord) => {
      const result: any = {
        round_close_date: value,
        [RoundEntryFields.rounds__round_close_date]: value
      }
      if (roundId) {
        result.id = roundId
      }
      return result
    }
  })

  return fields
}
